import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { HOME_THEME } from '@/components/home-a/theme';
import { useLanguage } from '@/contexts/LanguageContext';
import { DemoButton } from '@/components/demo/DemoCtas';
import type { DemoKind } from '@/components/demo/demoKinds';
import { useStartFree } from '@/components/site/StartFree';

/**
 * Bottom action bar for phones on public marketing pages.
 *
 * On a 375px screen the hero CTA scrolls away within the first swipe and the
 * next one is several screens down. This bar keeps the one action that
 * matters in thumb reach once the visitor is past the hero, and gets out of
 * the way when it would compete: near the footer (which has its own CTAs),
 * while a text field is focused (the keyboard is up), and on pages that are
 * themselves the conversion step.
 */
/**
 * Inner product pages lead to a free account (or the free trial once it is
 * released) and a sample report, not to the free audit (which stays on the
 * home page, guides, comparisons and the blog).
 */
const INNER: Array<[RegExp, DemoKind]> = [
  [/^\/(en\/|el\/)?solutions\/(ecommerce|shopify)\/?$/, 'cro'],
  [/^\/(en\/|el\/)?solutions\/lead-generation\/?$/, 'leadgen'],
  [/^\/(en\/|el\/)?solutions\/content-analysis\/?$/, 'content'],
  [/^\/(en\/|el\/)?solutions\/technical-analysis\/?$/, 'technical'],
  [/^\/(en\/|el\/)?solutions\/competitor-comparison\/?$/, 'competitor'],
  [/^\/(en\/|el\/)?features\/ai-heatmaps\/?$/, 'heatmap'],
  [/^\/(en\/|el\/)?features\/(neuromarketing|cro-techniques)\/?$/, 'cro'],
];

const HIDDEN_ON = [/^\/free-audit/, /^\/login/, /^\/signup/, /^\/reset-password/, /^\/checkout/, /^\/payment-success/, /^\/promo\//, /^\/website-audit-results/, /^\/free-cro/];

export default function MobileStickyCTA() {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const isEl = language === 'el';
  const inner = INNER.find(([re]) => re.test(pathname))?.[1] ?? null;
  const [pastHero, setPastHero] = useState(false);
  const [nearFooter, setNearFooter] = useState(false);
  const [typing, setTyping] = useState(false);
  const excluded = HIDDEN_ON.some((re) => re.test(pathname));
  const start = useStartFree();

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setNearFooter(e.isIntersecting), { rootMargin: '0px 0px 120px 0px' });
    io.observe(footer);
    return () => io.disconnect();
  }, [pathname]);

  useEffect(() => {
    const isField = (el: EventTarget | null) =>
      el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    const onIn = (e: FocusEvent) => isField(e.target) && setTyping(true);
    const onOut = () => setTyping(false);
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);

  const visible = !excluded && pastHero && !nearFooter && !typing;

  // Lets the chat button lift above the bar instead of sitting on top of it.
  useEffect(() => {
    document.body.dataset.mobileCta = visible ? 'on' : 'off';
    return () => {
      delete document.body.dataset.mobileCta;
    };
  }, [visible]);

  if (excluded) return null;

  return (
    <div
      aria-hidden={!visible}
      // The home page runs on its own palette (home-a/theme.ts); the bar sits
      // outside that wrapper, so it takes the same tokens there to match.
      style={pathname === '/' ? HOME_THEME : undefined}
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-md px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-transform duration-300 ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {inner ? (
        <div className="flex items-center gap-2" data-section="mobile_sticky">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground leading-tight">{start.facts[0]}</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">{start.facts[1]}</p>
          </div>
          <DemoButton kind={inner} location="mobile_sticky" className="h-11 shrink-0 rounded-full pl-1.5 pr-3.5 text-sm">
            {isEl ? 'Δείγμα' : 'Sample'}
          </DemoButton>
          <Link
            to={start.to}
            tabIndex={visible ? 0 : -1}
            data-track={start.trial ? 'start_trial' : 'start_signup'}
            className="inline-flex h-11 shrink-0 items-center gap-1.5 ec-cta rounded-full px-4 text-sm font-semibold"
          >
            {start.short}
          </Link>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground leading-tight">Free AI CRO audit</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">No card. Report in under 5 minutes.</p>
          </div>
          <Link
            to="/free-audit"
            tabIndex={visible ? 0 : -1}
            className="inline-flex h-11 shrink-0 items-center gap-1.5 ec-cta rounded-full px-5 text-sm font-semibold"
          >
            Analyse free

          </Link>
        </div>
      )}
    </div>
  );
}
